/**
 * Pure permission matching utilities
 *
 * These functions are framework-agnostic and have no dependencies.
 * They implement wildcard permission matching for authorization checks.
 *
 * @example
 * ```typescript
 * import { hasPermission, hasAnyPermission } from '@revalize/users/permissions'
 *
 * const userPerms = ['admin:*', 'profile:read']
 *
 * hasPermission(userPerms, 'admin:users')  // true (wildcard match)
 * hasPermission(userPerms, 'billing:read') // false
 * hasAnyPermission(userPerms, ['billing:read', 'profile:read']) // true
 * ```
 */

/**
 * Checks if a single held permission matches a required permission.
 * Supports wildcards: 'admin:*' matches 'admin:users', '*:*' matches everything.
 * Legacy bare scopes (e.g. 'admin') are treated as 'admin:*' for backward compatibility.
 *
 * @param held - Permission the user has
 * @param required - Permission being checked for
 * @returns true if held permission satisfies required permission
 */
export function matchesPermission(held: string, required: string): boolean {
  if (held === required) return true
  if (held === '*:*') return true

  // Treat bare scope names (no colon) as resource wildcards: 'admin' → 'admin:*'
  const normalizedHeld = held.includes(':') ? held : `${held}:*`

  const [heldResource, heldAction] = normalizedHeld.split(':')
  const [reqResource, reqAction] = required.split(':')

  if (heldResource === reqResource && heldAction === '*') return true
  if (heldResource === '*' && heldAction === reqAction) return true

  return false
}

/**
 * Checks if user has a specific permission (exact or wildcard match).
 *
 * @param userPermissions - Array of permissions the user has
 * @param required - Single permission to check for
 * @returns true if any held permission satisfies the required permission
 */
export function hasPermission(userPermissions: string[], required: string): boolean {
  return userPermissions.some((held) => matchesPermission(held, required))
}

/**
 * Checks if user has at least one of the required permissions.
 *
 * @param userPermissions - Array of permissions the user has
 * @param required - Array of permissions, any of which would satisfy the check
 * @returns true if user has at least one of the required permissions
 */
export function hasAnyPermission(userPermissions: string[], required: string[]): boolean {
  return required.some((perm) => hasPermission(userPermissions, perm))
}

/**
 * Checks if user has all of the required permissions.
 *
 * @param userPermissions - Array of permissions the user has
 * @param required - Array of permissions, all of which must be satisfied
 * @returns true if user has all required permissions
 */
export function hasAllPermissions(userPermissions: string[], required: string[]): boolean {
  return required.every((perm) => hasPermission(userPermissions, perm))
}
